import { FetchHttpClient, HttpClient } from "effect/http";
import { RpcClient, RpcSerialization } from "effect/rpc";
import {
  formatUtcInstant,
  systemClock,
} from "@ascleo/types/date-time-validity";
import { Effect, Layer } from "effect";
import { computeDcFriendHeader } from "../auth-headers";
import {
  normalizeBridgeUrlInput,
  normalizeSolTokenInput,
} from "../connection-settings-normalization";
import type {
  BridgeHttpHeaders,
  BridgeHttpRequestOptions,
  BridgeHttpResponse,
} from "../../platform/types";
import { createBoundedFetch, type FrontdeskFetch } from "./bounded-fetch";

let configuredBridgeUrl = "https://localhost:3010";
let configuredSolToken: string | null = null;

const E2E_MODE_KEY = "frontdesk-e2e-mode";
const LOCALHOST_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]"]);
type BridgeHttpRequest = (
  options: BridgeHttpRequestOptions,
) => Promise<BridgeHttpResponse>;

let configuredBridgeHttpRequest: BridgeHttpRequest | undefined;

/**
 * Defines host-owned exceptions for Bridge URL transport validation.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const policy: BridgeUrlSecurityPolicy = { allowHttpLocalhost: true };
 * ```
 */
export interface BridgeUrlSecurityPolicy {
  readonly allowHttpLocalhost: boolean;
}

let bridgeUrlSecurityPolicy: BridgeUrlSecurityPolicy = {
  allowHttpLocalhost: false,
};

function isFrontdeskE2EMode(): boolean {
  try {
    return globalThis.localStorage?.getItem(E2E_MODE_KEY) === "1";
  } catch {
    return false;
  }
}

function isSecureUrl(url: string): { secure: boolean; reason?: string } {
  try {
    const parsed = new URL(url);
    const isHttps = parsed.protocol === "https:";
    const isLocalhost = LOCALHOST_HOSTNAMES.has(parsed.hostname);
    const allowHttpLocalhost =
      bridgeUrlSecurityPolicy.allowHttpLocalhost || isFrontdeskE2EMode();

    if (isHttps) {
      return { secure: true };
    }

    if (parsed.protocol === "http:" && isLocalhost && allowHttpLocalhost) {
      return { secure: true };
    }

    if (parsed.protocol === "http:" && isLocalhost) {
      return {
        secure: false,
        reason:
          "HTTP localhost is only allowed in development mode. Use HTTPS in production.",
      };
    }

    return {
      secure: false,
      reason:
        "Transport encryption (HTTPS) is required for RPC communication with patient data.",
    };
  } catch {
    return { secure: false, reason: "Invalid URL format" };
  }
}

/**
 * Configures Bridge URL transport validation from the host app bootstrap.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * configureBridgeUrlSecurity({ allowHttpLocalhost: import.meta.env.DEV });
 * ```
 */
export function configureBridgeUrlSecurity(
  policy: BridgeUrlSecurityPolicy,
): void {
  bridgeUrlSecurityPolicy = policy;
}

/**
 * Selects a host-provided native HTTP transport for Bridge requests.
 *
 * Mobile hosts use the buffered JSON RPC endpoint so requests bypass WebView
 * CORS restrictions without requiring streaming response support.
 *
 * @since 1.1.10
 * @example
 * ```ts
 * configureBridgeHttpTransport(platform.bridgeHttpRequest)
 * ```
 */
export function configureBridgeHttpTransport(
  request: BridgeHttpRequest | undefined,
): void {
  configuredBridgeHttpRequest = request;
}

/**
 * Sets the validated Bridge base URL used by all FrontDesk+ RPC clients.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * setBridgeUrl("https://bridge.example.test");
 * ```
 */
export function setBridgeUrl(url: string): void {
  const normalizedUrl = normalizeBridgeUrlInput(url);
  const { secure, reason } = isSecureUrl(normalizedUrl);
  if (!secure) {
    throw new Error(`Insecure bridge URL: ${reason}`);
  }
  configuredBridgeUrl = normalizedUrl;
}

/**
 * Sets the SOL token used to authenticate Bridge requests.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * setSolToken(null);
 * ```
 */
export function setSolToken(token: string | null): void {
  configuredSolToken = normalizeSolTokenInput(token) ?? null;
}

/**
 * Returns the currently configured Bridge base URL.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const bridgeUrl = getBridgeUrl();
 * ```
 */
export function getBridgeUrl(): string {
  return configuredBridgeUrl;
}

function bridgeHeaders(headers: Headers): BridgeHttpHeaders {
  return Object.fromEntries(headers.entries());
}

function bridgeBody(body: BodyInit | null | undefined): string | undefined {
  if (body === null || body === undefined) {
    return undefined;
  }
  if (typeof body === "string") {
    return body;
  }
  if (body instanceof URLSearchParams) {
    return body.toString();
  }
  if (body instanceof ArrayBuffer) {
    return new TextDecoder().decode(body);
  }
  if (ArrayBuffer.isView(body)) {
    return new TextDecoder().decode(body);
  }
  throw new Error("The native Bridge transport only supports text request bodies");
}

function createNativeBridgeFetch(
  requestBridge: BridgeHttpRequest,
  timeoutMs?: number,
): typeof globalThis.fetch {
  const nativeFetch = async (...args: Parameters<typeof globalThis.fetch>) => {
    const [input, init] = args;
    const sourceRequest = input instanceof Request ? input : undefined;
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;
    const method = (init?.method ?? sourceRequest?.method ?? "GET").toUpperCase();
    if (method !== "GET" && method !== "POST") {
      throw new Error(`Unsupported native Bridge HTTP method: ${method}`);
    }

    const headers = new Headers(sourceRequest?.headers);
    new Headers(init?.headers).forEach((value, name) => {
      headers.set(name, value);
    });
    const response = await requestBridge({
      url,
      method,
      headers: bridgeHeaders(headers),
      body: bridgeBody(init?.body),
      timeoutMs,
    });

    return new Response(response.body, { status: response.status });
  };
  return Object.assign(nativeFetch, { preconnect: () => undefined });
}

/**
 * Creates a bounded fetch implementation with optional SOL authentication.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const bridgeFetch = createBridgeFetch(null);
 * ```
 */
export function createBridgeFetch(
  solToken: string | null,
  timeoutMs?: number,
): FrontdeskFetch {
  const baseFetch = configuredBridgeHttpRequest
    ? createNativeBridgeFetch(configuredBridgeHttpRequest, timeoutMs)
    : globalThis.fetch;
  const authenticatedFetch = Object.assign(
    async function authFetch(...args: Parameters<typeof globalThis.fetch>) {
      const [input, init] = args;
      const headers = new Headers(init?.headers);

      if (solToken) {
        const date = formatUtcInstant(systemClock.now());
        const dcFriend = await computeDcFriendHeader(solToken, date);
        headers.set("X-DC-FRIEND", dcFriend);
        headers.set("X-DC-DATE", date);
      }

      return baseFetch(input, { ...init, headers });
    },
    {
      preconnect: (url: unknown, options?: unknown) => {
        const nativePreconnect = Reflect.get(globalThis.fetch, "preconnect");
        if (typeof nativePreconnect === "function") {
          Reflect.apply(nativePreconnect, globalThis.fetch, [url, options]);
        }
      },
    },
  );

  return createBoundedFetch(authenticatedFetch, timeoutMs);
}

/**
 * Creates the bounded authenticated fetch for the active Bridge configuration.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const bridgeFetch = createConfiguredBridgeFetch();
 * ```
 */
export function createConfiguredBridgeFetch(timeoutMs?: number): FrontdeskFetch {
  return createBridgeFetch(configuredSolToken, timeoutMs);
}

/**
 * Creates the shared authenticated NDJSON RPC protocol for a Bridge base URL.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const layer = createDocCirrusRpcLayer("https://bridge.example.test");
 * ```
 */
export function createDocCirrusRpcLayer(baseUrl: string, timeoutMs?: number) {
  const usesNativeTransport = configuredBridgeHttpRequest !== undefined;
  const rpcUrl = `${baseUrl}${usesNativeTransport ? "/rpc-json" : "/rpc"}`;
  const bridgeFetch = createConfiguredBridgeFetch(timeoutMs);
  const fetchLayer = FetchHttpClient.layer.pipe(
    Layer.provide(Layer.succeed(FetchHttpClient.Fetch, bridgeFetch)),
  );
  const checkedFetchLayer = Layer.effect(
    HttpClient.HttpClient,
    Effect.map(HttpClient.HttpClient, HttpClient.filterStatusOk),
  ).pipe(Layer.provide(fetchLayer));
  return RpcClient.layerProtocolHttp({ url: rpcUrl }).pipe(
    Layer.provide([
      checkedFetchLayer,
      usesNativeTransport
        ? RpcSerialization.layerJson
        : RpcSerialization.layerNdjson,
    ]),
  );
}

/**
 * Creates the shared RPC protocol for the active Bridge configuration.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const layer = getDocCirrusRpcLayer();
 * ```
 */
export function getDocCirrusRpcLayer(timeoutMs?: number) {
  return createDocCirrusRpcLayer(configuredBridgeUrl, timeoutMs);
}
